import React, { FC, useState } from 'react';

import { Control, Controller } from 'react-hook-form';

import { Button, Text } from '@components/common';
import {
  BlockableComponent,
  BlockableComponents,
  Enums,
  Forms,
  getBlockLabel,
  isBlockOfKind,
  Models
} from '@components/SurveyBuilder';
import { Tooltip } from '@components/Tooltip';
import { ImageRenderer } from '@components/Unmoderated/components/ImageRenderer';
import { TaskHeader } from '@components/Unmoderated/components/TaskHeader';

import { useUnmoderatedContext } from '../hooks/useUnmoderatedContext';
import * as Types from '../types';
import { validateAnswer } from '../utils';

import { BlockableComponentRenderer } from './BlockableComponentRenderer';
import { ResponsiveCard } from './ResponsiveCard';
import { TaskLoadedBlocker } from './TaskLoadedBlocker';

interface Props {
  block: Models.Block;
  control: Control<Types.FormData>;
  isValid: boolean;
  setInteractionModalOpen: (open: boolean) => void;
  onSubmit: () => void;
  isDirty?: boolean;
}

const EMPTY_BLOCKABLE_BLOCKS: `${Enums.Kind}`[] = [Enums.Kind.info, Enums.Kind.welcome, Enums.Kind.thankYou];

export const Question: FC<Props> = ({ block, control, isValid, setInteractionModalOpen, onSubmit, isDirty }) => {
  const { blocks, deviceType, topBarUnmoderatedLayout } = useUnmoderatedContext();
  const [blocker, setBlocker] = useState(false);
  const [taskInProgress, setTaskInProgress] = useState<boolean>(false);

  const [isMinimized, setIsMinimized] = useState(false);

  const onNextTask = () => {
    if (blocker || (!EMPTY_BLOCKABLE_BLOCKS.includes(block.kind) && !isDirty)) {
      setInteractionModalOpen(true);
    } else {
      onSubmit?.();
    }
  };

  const renderer: Forms.FormController = ({ value, onChange }) => {
    if (!block) {
      return <></>;
    }

    const { kind } = block;

    if (EMPTY_BLOCKABLE_BLOCKS.includes(kind)) {
      return <></>;
    }

    if (isBlockOfKind(block, Enums.Kind.linearScale)) {
      return (
        <BlockableComponent<BlockableComponents.LinearScale>
          kind={kind}
          componentProps={{
            onChange,
            options: block.blockable.options,
            className: 'mt-4'
          }}
        />
      );
    }

    if (isBlockOfKind(block, Enums.Kind.singleSelect)) {
      return (
        <BlockableComponent<BlockableComponents.SingleSelect>
          kind={kind}
          componentProps={{
            onChange,
            options: block.blockable.options,
            other: block.blockable.other,
            wrapperClassName: topBarUnmoderatedLayout ? 'mt-4' : undefined
          }}
        />
      );
    }

    if (isBlockOfKind(block, Enums.Kind.multiSelect)) {
      return (
        <BlockableComponent<BlockableComponents.MultiSelect>
          kind={kind}
          componentProps={{
            onChange,
            options: block.blockable.options,
            other: block.blockable.other,
            none_of_above: block.blockable.none_of_above,
            wrapperClassName: topBarUnmoderatedLayout ? 'mt-4' : undefined
          }}
        />
      );
    }

    if (isBlockOfKind(block, Enums.Kind.yesNo)) {
      return (
        <BlockableComponent<BlockableComponents.YesNo>
          kind={kind}
          componentProps={{
            onChange,
            options: ['Yes', 'No'],
            wrapperClassName: topBarUnmoderatedLayout ? 'mt-4' : undefined
          }}
        />
      );
    }

    if (isBlockOfKind(block, Enums.Kind.date)) {
      return (
        <BlockableComponent<BlockableComponents.Date>
          kind={kind}
          componentProps={{
            onChange: (date) => onChange(date?.toString()),
            selected: value && typeof value === 'string' ? new Date(value) : null
          }}
        />
      );
    }

    return <BlockableComponent kind={kind} componentProps={{ onChange }} />;
  };

  if (!block) {
    return <></>;
  }

  if (deviceType === 'desktop' && topBarUnmoderatedLayout) {
    return (
      <div className='flex h-screen w-full flex-col overflow-hidden bg-white pt-4'>
        <TaskHeader
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          block={block}
          renderQuestion={() => (
            <Controller
              control={control}
              key={block.id}
              name='value'
              render={(props) => (
                <div data-testid='block_body_component'>
                  <BlockableComponentRenderer block={block} isMinimized={isMinimized} {...props} />
                </div>
              )}
              rules={{
                required: block.required,
                validate: validateAnswer(block)
              }}
            />
          )}
          renderOptions={() => (
            <Tooltip placement='bottom' content='Required to continue' isDisabled={isValid}>
              <Button
                aria-label='Next task'
                className={
                  taskInProgress || EMPTY_BLOCKABLE_BLOCKS.includes(block.kind) || isDirty
                    ? 'btn-custom-brand'
                    : 'btn-custom-brand-secondary'
                }
                iconSuffix='arrowRight'
                type='button'
                onClick={onNextTask}
                disabled={!isValid}
                small
                noStyle
              >
                Next task
              </Button>
            </Tooltip>
          )}
        />

        <div className='relative flex w-full flex-1 overflow-hidden bg-gray-50'>
          <div className='h-full w-full'>
            {block.image_url ? (
              <ImageRenderer
                block={block}
                onLoad={() => setBlocker(true)}
                alt='Question attachment'
                wrapperClassName='h-full w-full'
                className='h-full w-full'
                useNaturalSize={true}
              />
            ) : (
              <div className='flex h-full w-full items-center justify-center'>
                <Text italic h='400'>
                  No additional content on this page.
                </Text>
              </div>
            )}
          </div>
          {blocker && (
            <TaskLoadedBlocker
              blockKind={block.kind}
              onBeginTask={() => {
                setTaskInProgress(true);
                setBlocker(false);
                setIsMinimized(true);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <ResponsiveCard
      blockKind={getBlockLabel(block.kind)}
      blockPosition={block.position}
      description={block.description}
      title={block.title}
      totalBlocks={blocks.length}
      renderFooter={() => (
        <Button
          aria-label='Continue'
          className='btn-custom-brand'
          icon='arrowRight'
          type='submit'
          disabled={!isValid}
          small
          noStyle
        >
          Continue
        </Button>
      )}
    >
      <div className='my-4'>
        <Controller
          control={control}
          key={block.id}
          name='value'
          render={renderer}
          rules={{
            required: block.required,
            validate: validateAnswer(block)
          }}
        />
      </div>
    </ResponsiveCard>
  );
};
